import moment from "moment";

import { BaseAPI } from "./BaseAPI";
import { API_DATE_FORMAT } from "../constants/dateFormat";
import AdminUser from "../models/AdminUser";

import type {
  AddModeratorResponse,
  AdminUserToCreate,
  CreateOrUpdateAdminUserResponse,
  DeleteAdminUserRolesParams,
  DeleteAdminUserRolesResponse,
  DeleteModeratorResponse,
  GetAdminProjectsResponse,
  GetAdminUserListResponse,
  GetAdminUserRolesResponse,
  GetModeratorUserListResponse,
  GetSingleAdminUserResponse,
  GetUserListResponse,
  GetUserProfileResponse,
  GetUserSearchResponse,
  ImpersonateUserParams,
  ImpersonateUserResponse,
  PostAdminUserRolesParams,
  PostAdminUserRolesResponse,
  PutAdminUserRolesParams,
  PutAdminUserRolesResponse,
  SetNewPasswordParams,
  UpdateOrganizationParams,
  UpdateOrganizationParamsResponse,
  UpdateUserPreferencesParams,
  UpdateUserPreferencesResponse,
} from "./AdminUserAPI.types";
import type { APIConf } from "./BaseAPI";

class AdminUserAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "user";
    this.version = "v3";
  }

  async getAdminUserList(): Promise<GetAdminUserListResponse> {
    return this.axios
      .get<GetAdminUserListResponse>(`${this.routeBase}/admin/editors`)
      .then((response) => response.data);
  }

  async getSingleAdminUser(
    userId: string | number,
  ): Promise<GetSingleAdminUserResponse> {
    if (!userId) return Promise.reject(new Error("userId is required"));

    return this.axios
      .get<GetSingleAdminUserResponse>(
        `${this.routeBase}/admin/editors/${userId}`,
      )
      .then((response) => response.data);
  }

  async getAdminProjects(): Promise<GetAdminProjectsResponse> {
    return this.axios
      .get<GetAdminProjectsResponse>(
        `${this.routeBase}/admin/editors/projects/`,
      )
      .then((response) => response.data);
  }

  async updateAdminUser(
    user: AdminUser | AdminUserToCreate,
  ): Promise<CreateOrUpdateAdminUserResponse> {
    if (!(user instanceof AdminUser))
      return Promise.reject(new Error("user is not an instance of AdminUser")); // for non-TS legacy files

    const { uuid } = user;
    const userData = user.toAPI();
    if (!user.uuid) return this.createAdminUser(user);
    return this.axios
      .put<CreateOrUpdateAdminUserResponse>(
        `${this.routeBase}/admin/editors/${uuid}`,
        userData,
      )
      .then((response) => response.data);
  }

  async createAdminUser(
    user: AdminUser,
  ): Promise<CreateOrUpdateAdminUserResponse> {
    if (!(user instanceof AdminUser))
      return Promise.reject(new Error("user is not an instance of AdminUser")); // for non-TS legacy files

    const userData = user.toAPI();

    return this.axios
      .post<CreateOrUpdateAdminUserResponse>(
        `${this.routeBase}/admin/editors`,
        userData,
      )
      .then((response) => response.data);
  }

  async getAdminUserRoles(
    userId: string | number,
  ): Promise<GetAdminUserRolesResponse> {
    if (!userId) return Promise.reject(new Error("userId is required"));

    return this.axios
      .get<GetAdminUserRolesResponse>(
        `${this.routeBase}/admin/editors/${userId}/roles`,
      )
      .then((response) => response.data);
  }

  async deleteAdminUserRoles({
    userId,
    projectId,
    channelId,
    organizationId,
  }: DeleteAdminUserRolesParams): Promise<DeleteAdminUserRolesResponse> {
    return this.axios
      .delete<DeleteAdminUserRolesResponse>(
        `${this.routeBase}/admin/editors/${userId}/roles`,
        {
          data: {
            storefront_project_uuid: projectId,
            organization_uuid: organizationId,
            fast_channel_uuid: channelId,
          },
        },
      )
      .then((response) => response.data);
  }

  async putAdminUserRoles({
    userId,
    projectId,
    roles,
    channelId,
    organizationId,
  }: PutAdminUserRolesParams): Promise<PutAdminUserRolesResponse> {
    return this.axios
      .put<PutAdminUserRolesResponse>(
        `${this.routeBase}/admin/editors/${userId}/roles`,
        {
          storefront_project_uuid: projectId,
          organization_uuid: organizationId,
          fast_channel_uuid: channelId,
          roles,
        },
      )
      .then((response) => response.data);
  }

  async postAdminUserRoles({
    userId,
    projectId,
    roles,
    channelId,
    organizationId,
  }: PostAdminUserRolesParams): Promise<PostAdminUserRolesResponse> {
    return this.axios
      .post<PostAdminUserRolesResponse>(
        `${this.routeBase}/admin/editors/${userId}/roles`,
        {
          storefront_project_uuid: projectId,
          organization_uuid: organizationId,
          fast_channel_uuid: channelId,
          roles,
        },
      )
      .then((response) => response.data);
  }

  async deleteAdminUser(userId: string | number): Promise<unknown> {
    if (!userId) return Promise.reject(new Error("userId is required"));
    return this.axios
      .delete(`${this.routeBase}/admin/editors/${userId}`)
      .then((response) => response.data);
  }

  async impersonateUser({
    userId,
    projectSlug,
  }: ImpersonateUserParams): Promise<ImpersonateUserResponse> {
    return this.axios
      .post<ImpersonateUserResponse>(
        `${this.apiBase}/${this.service}/v4/admin/impersonate_user`,
        {
          user_uuid: userId,
        },
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async getModeratorUserList(
    projectSlug: string,
  ): Promise<GetModeratorUserListResponse> {
    return this.axios
      .get<GetModeratorUserListResponse>(
        `${this.apiBase}/${this.service}/v4/admin/list_moderators`,
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async deleteModerator(
    projectSlug: string,
    userUuid: string,
  ): Promise<DeleteModeratorResponse> {
    return this.axios
      .delete<DeleteModeratorResponse>(
        `${this.apiBase}/${this.service}/v4/admin/users/moderator/${userUuid}`,
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async addModerator(
    projectSlug: string,
    uuid: string,
  ): Promise<AddModeratorResponse> {
    return this.axios
      .post<AddModeratorResponse>(
        `${this.apiBase}/${this.service}/v4/admin/users/moderator/${uuid}`,
        {},
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async getUserSearch(
    search: string,
    projectSlug: string,
  ): Promise<GetUserSearchResponse> {
    return this.axios
      .post<GetUserSearchResponse>(
        `${this.apiBase}/${this.service}/v4/admin/search_users`,
        {
          search,
        },
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async getUserList(params: {
    page?: number;
    perPage?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    subscriptionStatus?: string | null;
    order?: { direction: "up" | "down"; column: string };
    projectSlug: string;
  }): Promise<GetUserListResponse> {
    const order = {
      direction: params.order?.direction ?? "down",
      field: params.order?.column ?? "updated_at",
    };
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 10;
    const search = params.search ?? null;
    const filter = {
      begin_date: moment(params.startDate).format(API_DATE_FORMAT) ?? null,
      end_date: moment(params.endDate).format(API_DATE_FORMAT) ?? null,
      status: "enabled",
      subscription_status: params?.subscriptionStatus ?? null,
    };
    const projectSlug = params?.projectSlug;

    return this.axios
      .post<GetUserListResponse>(
        `${this.apiBase}/${this.service}/v4/admin/users`,
        {
          per_page: perPage,
          page,
          filter,
          order,
          search,
        },
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async getUserProfile(): Promise<GetUserProfileResponse> {
    return this.axios
      .get<GetUserProfileResponse>(`${this.routeBase}/profile`)
      .then((response) => response.data);
  }

  async updateUserPassword({
    currentPassword,
    newPassword,
    invalidateSessions = true,
  }: {
    currentPassword: string;
    newPassword: string;
    invalidateSessions?: boolean;
  }): Promise<void> {
    return this.axios.post(`${this.apiBase}/${this.service}/v2/password`, {
      oldPassword: currentPassword,
      newPassword,
      invalidateSessions,
    });
  }

  async resetPassword({
    email,
    resetRoute,
    language,
  }: {
    email: string;
    resetRoute: string;
    language: string;
  }): Promise<string> {
    return this.axios
      .post<string>(
        `${this.apiBase}/${this.service}/v2/admin/password/forgot`,
        {
          email,
          reset_route: resetRoute,
          lang: language,
          invalidateSessions: true,
        },
      )
      .then((response) => response.data);
  }

  async setNewPassword({
    password,
    resetToken,
  }: SetNewPasswordParams): Promise<string> {
    return this.axios
      .post<string>(`${this.apiBase}/${this.service}/v2/admin/password/reset`, {
        password,
        reset_token: resetToken,
        invalidateSessions: true,
      })
      .then((response) => response.data);
  }

  async updateUserPreferences({
    encodingNotifications,
    theme, // TODO backend not ready
  }: UpdateUserPreferencesParams): Promise<UpdateUserPreferencesResponse> {
    return this.axios
      .put<UpdateUserPreferencesResponse>(
        `${this.apiBase}/${this.service}/v4/admin/user_preferences`,
        {
          notif_encoding_finished_mail: encodingNotifications.email,
          notif_encoding_finished_bo: encodingNotifications.inApp,
          theme,
        },
      )
      .then((response) => response.data);
  }

  async updateOrganizationParams({
    encodingNotifications,
  }: UpdateOrganizationParams): Promise<UpdateOrganizationParamsResponse> {
    return this.axios
      .put<UpdateOrganizationParamsResponse>(
        `${this.apiBase}/${this.service}/v4/admin/organization_params`,
        {
          notif_encoding_finished_zapier: encodingNotifications.zapier,
        },
      )
      .then((response) => response.data);
  }
}

const AdminUserAPI = new AdminUserAPIClass({
  token: localStorage.token,
});

export default AdminUserAPI;
